import configJSON from 'config.json';
import ExpCommercialTheme from '../us/theme-config';
import { CountryCodeList } from 'types/countries';
import { SiteLanguage } from 'types/locales';
import {
  EXP_COMMERCIAL_CA_ROOT_DOMAIN,
  ZOOCASA_EXP_TEST_CA_ROOT_DOMAIN,
} from 'constants/domains';
import {
  AREA_LISTINGS_ROUTE,
  EMBER_ROUTE,
  ROOT_ROUTE,
} from 'components/dynamic-page/route-matchers/types';
import { SEARCH_PAGE_ROUTE_NAME } from 'types/search_page_types';
import { createHeaderConfig } from './header-config';
import { createHomeConfig } from './home-config';
import { createFooterConfig } from './footer-config';
import { createOrganizationConfig } from './organization-config';
import { homeRouteHandler } from '../us/route-handler';
import { agentSearchConfig as agentSearch } from '../us/search-agent-config';
import { createQuoteConfig } from '../us/quote-config';
import { Colors } from 'constants/colors';
import { benefitsConfig } from './benefits-config';
import { metricsConfig } from './metrics-config';
import { getInTouchConfig } from './get-in-touch-config';

import {
  EXP_COMMERCIAL_CA_TENANT_ID,
  EXP_COMMERCIAL_CA_TENANT_ID_TYPE,
  Tenant,
} from '@tenants/types';
import { searchOurPropertiesConfig } from './search-our-properties-config';
import { Namespaces } from 'types/locales';
import { createLanguageConfig } from '@tenants/utils/configs';

import type {
  ContentHosts,
  FooterConfig,
  HeaderConfig,
  HomeConfig,
  OrganizationConfig,
  TenantConfigAgentSearch,
  TenantConfigContactUs,
  TenantConfigExpCommercial,
  TenantConfigFilters,
  TenantConfigFooter,
  TenantConfigHeader,
  TenantConfigHome,
  TenantConfigOrganization,
} from '@tenants/types';
import { createCommercialFaqData } from './faq-config';
import { contactUsConfig } from './contact-us-config';
import { listingConfig } from '../us/listing-config';

//#region Public Constants

export const EXP_COMMERCIAL_CA_L18N_NAMESPACES = [
  Namespaces.AGENT_SEARCH,
  Namespaces.EXP_COMMERCIAL_CA,
  Namespaces.COMMON,
  Namespaces.SEARCH,
  Namespaces.FORM_ERROR,
  Namespaces.MODAL,
  Namespaces.LISTING_EXP_COMMERCIAL,
  Namespaces.LISTING,
  Namespaces.HOME_APPRAISAL,
  Namespaces.CONNECTION_REQUEST,
  Namespaces.CONNECT_WITH_AGENT,
] as const;

//#endregion

//#region Config Factories
const header: HeaderConfig = createHeaderConfig(EXP_COMMERCIAL_CA_ROOT_DOMAIN);
const home: HomeConfig = createHomeConfig(EXP_COMMERCIAL_CA_ROOT_DOMAIN);
const footer: FooterConfig = createFooterConfig(EXP_COMMERCIAL_CA_ROOT_DOMAIN);
const organization: OrganizationConfig = createOrganizationConfig(EXP_COMMERCIAL_CA_ROOT_DOMAIN);
//#endregion

//#region Public Types
export type ExpCommercialCaTenantConfig = TenantConfigHeader & TenantConfigHome & TenantConfigFooter & TenantConfigOrganization & TenantConfigAgentSearch & TenantConfigExpCommercial & TenantConfigFilters & TenantConfigContactUs;
export type ExpCommercialCaTenant = Tenant<EXP_COMMERCIAL_CA_TENANT_ID_TYPE, typeof ExpCommercialTheme, ExpCommercialCaTenantConfig>;

//#endregion

const allowGlobalLocaleSwitching = false;

/**
 * The eXp Commercial Canada tenant.
 *
 * This tenant is used to render the eXp Commercial Canada website.
 */
export const expCommercialCaTenant: ExpCommercialCaTenant = {
  id: EXP_COMMERCIAL_CA_TENANT_ID,
  hosts: configJSON.hosts[EXP_COMMERCIAL_CA_TENANT_ID],
  contentHosts: configJSON.contentPageApi[EXP_COMMERCIAL_CA_TENANT_ID] as ContentHosts,
  blogApiUrl: configJSON.blogApi[EXP_COMMERCIAL_CA_TENANT_ID],
  blogContentPageUrl: configJSON.blogContentPageUrl[EXP_COMMERCIAL_CA_TENANT_ID],
  theme: ExpCommercialTheme,
  get localization() {
    return {
      ...createLanguageConfig({
        globalLanguages: [SiteLanguage.EnglishCA],
        standardLanguages: [SiteLanguage.EnglishCA],
        globalDefaultLanguage: SiteLanguage.EnglishCA,
        standardDefaultLanguage: SiteLanguage.EnglishCA,
        namespaces: EXP_COMMERCIAL_CA_L18N_NAMESPACES,
        allowGlobalLocaleSwitching,
      }),
      countries: [CountryCodeList.CANADA],
      defaultCountry: CountryCodeList.CANADA,
    };
  },
  config: {
    header,
    home,
    footer,
    organization,
    agentSearch,
    commercial: {
      quotes: {
        leo: createQuoteConfig(Colors.expCommercialGreen, 'highlight-green'),
      },
      metrics: metricsConfig,
      benefits: benefitsConfig,
      searchOurProperties: searchOurPropertiesConfig,
      faq: createCommercialFaqData(),
      listing: listingConfig,
      getInTouch: getInTouchConfig,
    },
    filters: {
      hideExpiredListings: true,
      countryCode: CountryCodeList.CANADA,
    },
    contactUs: contactUsConfig,
  },
  routes: {
    claimed: [ROOT_ROUTE, SEARCH_PAGE_ROUTE_NAME],
    excluded: [EMBER_ROUTE, AREA_LISTINGS_ROUTE],
    handlers: [
      {
        match: ROOT_ROUTE,
        handler: homeRouteHandler,
      },
    ],
  },
  debug: {
    forceTenantHeaderValue: `useTenant="${EXP_COMMERCIAL_CA_TENANT_ID}"`,
    forceTenantFeatureValue: EXP_COMMERCIAL_CA_TENANT_ID,
  },
  features: {
    MyLinkMyLead: false,
    AgentSearch: false,
    AccessibilityWidget: false,
  },
  googleTagManager: {
    containerIds: {
      // TODO: commercial-ca: uncomment this when we go live
      defaultContainerId: '',
      // defaultContainerId: configJSON.googleTagManager.containerIdExpCommercialCa,
    },
  },
  sso: {
    googleWebClientId: process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID || '', // TODO: Add the correct client ID
    facebookAppId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID || '', // TODO: Add the correct app ID
  },
  sitemap: {
    urls: [
      'https://www.expcommercial.ca/api/insights-sitemap.xml',
    ],
  },
  robots: {
    disallowDomains: [
      `commercial.${ZOOCASA_EXP_TEST_CA_ROOT_DOMAIN}`,
      `www.${EXP_COMMERCIAL_CA_ROOT_DOMAIN}`,
    ],
  },
  pvr: {
    phoneNumber: '1-844-539-0722',
  },
  mobileAppBanner: null,
};


